import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { submitEnquiry } from "../utils/submitEnquiry";

import greenBackground from "../../assets/background-images/green-bg.png";
import enquiryImage from "../../assets/E6.jpg";

export default function Enquiry() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    msg: "",
  });

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.phone || !/^\d{10}$/.test(form.phone))
      e.phone = "Enter a valid 10-digit phone number";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    return e;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError("");
    try {
      await submitEnquiry({ name: form.name, email: form.email, mobile: form.phone, notes: form.msg });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setForm({ name: "", phone: "", email: "", msg: "" });
      setErrors({});
    } catch {
      setApiError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = `
    w-full
    h-[50px]
    rounded-[12px]
    border
    border-[#4D665B]
    bg-transparent
    px-4
    text-[16px]
    text-[#183B2D]
    placeholder:text-[#6F7D74]
    outline-none
    transition
    focus:border-[#183B2D]
  `;

  return (
    <section
      id="enquire"
      data-testid="enquiry-section"
      className="relative py-[75px] overflow-hidden"
      style={{
        backgroundImage: `url(${greenBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="relative z-10 max-w-[1040px] mx-auto px-4">
        {/* Desktop Layout */}
        <div
          className="
            hidden
            lg:grid
            grid-cols-[560px_400px]
            gap-[40px]
            justify-center
            items-center
          "
        >
          {/* Left Image */}
          <motion.div
            initial={{ opacity: 0, x: -25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="
              relative
              overflow-hidden
              rounded-[24px]
              h-[485px]
            "
          >
            <img
              src={enquiryImage}
              alt="38 Daffodils Building"
              draggable={false}
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, x: 25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="
              bg-[#D9DCB1]
              rounded-[24px]
              px-[30px]
              pt-[22px]
              pb-[24px]
              h-[485px]
              flex
              flex-col
            "
          >
            <h2
              className="
                text-[#183B2D]
                text-[24px]
                font-semibold
                leading-none
                mb-[14px]
              "
            >
              ENQUIRE NOW
            </h2>

            <p
              className="
                text-[#183B2D]
                text-[16px]
                leading-[1.45]
                mb-[18px]
                max-w-[290px]
              "
            >
              Please enter the details below to get in touch with us!
            </p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center flex-1">
                <CheckCircle2
                  size={60}
                  strokeWidth={1.5}
                  className="text-[#183B2D] mb-4"
                />

                <h3 className="text-[#183B2D] text-2xl font-semibold mb-2">
                  Thank You!
                </h3>

                <p className="text-[#183B2D]/70 text-center">
                  We'll get in touch with you shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="flex flex-col gap-[18px]"
              >
                {/* Name */}
                <input
                  required
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className={fieldClass}
                />

                {/* Phone */}
                <div>
                  <div
                    className={`
                      h-[50px] rounded-[12px] border flex items-center overflow-hidden
                      ${errors.phone ? "border-red-500" : "border-[#4D665B]"}
                    `}
                  >
                    <div className="flex items-center gap-1 px-3">
                      <span className="text-[24px]">🇮🇳</span>
                      <ChevronDown size={14} className="text-[#183B2D]" />
                    </div>
                    <input
                      required
                      type="tel"
                      placeholder="Phone"
                      maxLength={10}
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      className="flex-1 bg-transparent outline-none text-[#183B2D] placeholder:text-[#6F7D74] text-[16px] pr-4"
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-[11px] mt-1 pl-1">{errors.phone}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <input
                    required
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    className={`${fieldClass} ${errors.email ? "border-red-500" : ""}`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-[11px] mt-1 pl-1">{errors.email}</p>
                  )}
                </div>

                {/* Comments */}
                <input
                  type="text"
                  placeholder="Comments"
                  value={form.msg}
                  onChange={(e) => setField("msg", e.target.value)}
                  className={fieldClass}
                />

                {apiError && (
                  <p className="text-red-500 text-[12px] text-center">{apiError}</p>
                )}

                {/* Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="
                    w-full h-[54px] rounded-[12px]
                    bg-[#143B31] text-[#D9DCB1]
                    text-[18px] font-semibold mt-[2px]
                    transition hover:opacity-95
                    disabled:opacity-60 disabled:cursor-not-allowed
                  "
                >
                  {submitting ? "Submitting..." : "ENQUIRE NOW"}
                </button>
              </form>
            )}
          </motion.div>
        </div>

{/* Mobile Layout */}
<div className="lg:hidden flex justify-center px-2">
  <div
    className="
      bg-[#D9DCB1]
      rounded-[24px]
      w-full
      max-w-[360px]
      px-3
      pt-4
      pb-4
    "
  >
    <h2
      className="
        text-[#183B2D]
        text-[20px]
        font-semibold
        mb-3
      "
    >
      ENQUIRE NOW
    </h2>

    <p
      className="
        text-[#183B2D]
        text-[12px]
        leading-[1.4]
        mb-3
      "
    >
      Please enter the details below to get in touch with us!
    </p>

    {submitted ? (
      <div className="flex flex-col items-center justify-center py-2">
        <CheckCircle2
          size={54}
          strokeWidth={1.5}
          className="text-[#183B2D] mb-3"
        />

        <h3 className="text-[#183B2D] text-xl font-semibold mb-2">
          Thank You!
        </h3>

        <p className="text-[#183B2D]/70 text-center text-sm">
          We'll get in touch with you shortly.
        </p>
      </div>
    ) : (
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        {/* Name */}
        <input
          required
          type="text"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          className="
            h-[52px] rounded-[12px] border border-[#183B2D]
            bg-transparent px-4 text-[16px] text-[#183B2D]
            placeholder:text-[#6F7D74] outline-none
          "
        />

        {/* Phone */}
        <div>
          <div
            className={`
              h-[52px] rounded-[12px] border flex items-center overflow-hidden
              ${errors.phone ? "border-red-500" : "border-[#183B2D]"}
            `}
          >
            <div className="flex items-center gap-1 px-4">
              <span className="text-[22px]">🇮🇳</span>
              <ChevronDown size={14} className="text-[#183B2D]" />
            </div>
            <input
              required
              type="tel"
              placeholder="Phone"
              maxLength={10}
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              className="flex-1 bg-transparent outline-none text-[#183B2D] placeholder:text-[#6F7D74] text-[16px] pr-4"
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-[11px] mt-1 pl-1">{errors.phone}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            className={`
              h-[52px] rounded-[12px] border bg-transparent px-4
              text-[16px] text-[#183B2D] placeholder:text-[#6F7D74] outline-none
              ${errors.email ? "border-red-500" : "border-[#183B2D]"}
            `}
          />
          {errors.email && (
            <p className="text-red-500 text-[11px] mt-1 pl-1">{errors.email}</p>
          )}
        </div>

        {/* Comments */}
        <input
          type="text"
          placeholder="Comments"
          value={form.msg}
          onChange={(e) => setField("msg", e.target.value)}
          className="
            h-[52px] rounded-[12px] border border-[#183B2D]
            bg-transparent px-4 text-[16px] text-[#183B2D]
            placeholder:text-[#6F7D74] outline-none
          "
        />

        {apiError && (
          <p className="text-red-500 text-[12px] text-center">{apiError}</p>
        )}

        {/* Button */}
        <button
          type="submit"
          disabled={submitting}
          className="
            h-[56px] rounded-[12px]
            bg-[#143B31] text-[#D9DCB1]
            text-[18px] font-semibold mt-1
            disabled:opacity-60 disabled:cursor-not-allowed
          "
        >
          {submitting ? "Submitting..." : "ENQUIRE NOW"}
        </button>
      </form>
    )}
  </div>
</div>
        <p className="text-right text-[#E2E4B0]/70 text-[9px] lg:text-[11px] mt-4 pr-1">
          Artistic Impression | *T &amp; C&apos;s Apply
        </p>
      </div>
    </section>
  );
}
